import { useEffect, useRef, useState } from 'react';
import { readFlagAudio } from '../../app/flags';
import { log } from '../../app/logger';

/**
 * B-13 — **playback for one flag** (design spec §2.4; scenario 15; AUD-5: playback IS the v1
 * contract — no transcription exists to caption from).
 *
 * ── Loaded on demand, never with the list ───────────────────────────────────
 *
 * A break can hold a dozen flags, each up to `VOICE_NOTE_MAX_S` of audio. Nothing is read until
 * the user taps Play; the tap mounts a native `<audio controls>` — a deliberate tap is the whole
 * interaction, because audio that starts on its own plays out loud at a touchline at the wrong
 * moment (no auto-play, ever).
 *
 * ── The object URL is created and revoked in the same component ─────────────
 *
 * `app/flags.readFlagAudio` returns the `Blob` because the URL's lifetime belongs to whoever
 * mounts the element: `createObjectURL` without its `revokeObjectURL` pins the whole recording
 * for the life of the document. Created here, revoked here — on unmount AND on replacement (the
 * queue reuses this component across flags). This is the platform's one correct shape and
 * parallels A's `FlagPlayback` deliberately (duplication-ledger candidate: coincidental
 * boilerplate; B renders B's classes and copy).
 *
 * ── No duration is ever read from the blob ──────────────────────────────────
 *
 * A timesliced recording container reports no duration (`canplaythrough` fires with
 * `duration` Infinity/NaN — the hard-won A finding), so this component renders no length of its
 * own anywhere: the native control is trusted with what the container honestly reports, and the
 * flag's identity is its minute, which the queue row already carries. Likewise no playability
 * probe gates the button — WebKit's probe false-negatives without user activation are real, and
 * the user's own ear on a Play tap outranks a probe verdict (A's precedent).
 */
export interface FlagPlaybackBProps {
  readonly audioBlobId: string;
  /** What a screen reader is told this recording is — "the 34′ flag". */
  readonly label: string;
}

export function FlagPlaybackB({ audioBlobId, label }: FlagPlaybackBProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const created = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (created.current !== null) {
        URL.revokeObjectURL(created.current);
        created.current = null;
      }
    };
  }, [url]);

  if (url !== null) {
    return (
      <audio className="flb-audio" data-testid="flb-audio" aria-label={label} controls src={url} />
    );
  }

  return (
    <>
      <button
        type="button"
        className="flb-play"
        data-testid="flb-play"
        onClick={() => {
          setProblem(null);
          void readFlagAudio(audioBlobId).then(
            (blob) => {
              if (blob === null) {
                // §8.2's honest degradation at the moment the user asked: the row said available
                // when the list was built; if it is not now, a silent button would be a control
                // that does nothing.
                setProblem('That recording could not be played.');
                return;
              }
              const next = URL.createObjectURL(blob);
              created.current = next;
              setUrl(next);
            },
            (cause: unknown) => {
              log.error('ui-b/flags', 'the flag audio could not be read', {
                audioBlobId,
                message: cause instanceof Error ? cause.message : String(cause),
              });
              setProblem('That recording could not be read.');
            },
          );
        }}
      >
        Play
      </button>
      {problem !== null && (
        <p className="flb-problem" role="status" data-testid="flb-problem">
          {problem}
        </p>
      )}
    </>
  );
}
